// Image processing utilities for book photos

export const isImageFile = (file) => file.type.startsWith('image/') || /\.(heic|heif|webp|jpg|jpeg|png|gif|bmp|tiff?)$/i.test(file.name);

// Normalize image formats for book uploads.
//   - HEIC / WebP / etc. \u2192 re-encoded to JPEG via canvas
//   - Large JPEG / PNG (>1.5 MB) \u2192 downscaled to MAX_WIDTH and re-encoded JPEG q88
//   - Small JPEG / PNG (\u22641.5 MB) \u2192 pass through unchanged
// Side effects: downscaling also strips EXIF and bakes in orientation, so iPhone
// rotated photos render correctly downstream.
const NORMALIZE_MAX_WIDTH = 1600;
const NORMALIZE_SIZE_GATE_BYTES = 1.5 * 1024 * 1024;
const NORMALIZE_QUALITY = 0.88;

export const normalizeImageForBook = (file) => new Promise((resolve, reject) => {
  const isStandard = file.type === 'image/jpeg' || file.type === 'image/png';
  // Pass-through: small standard formats are already in good shape
  if (isStandard && file.size <= NORMALIZE_SIZE_GATE_BYTES) return resolve(file);

  const url = URL.createObjectURL(file);
  const img = new window.Image();
  img.onload = () => {
    try {
      const srcW = img.naturalWidth;
      const srcH = img.naturalHeight;
      const scale = srcW > NORMALIZE_MAX_WIDTH ? NORMALIZE_MAX_WIDTH / srcW : 1;
      const dstW = Math.round(srcW * scale);
      const dstH = Math.round(srcH * scale);
      const canvas = document.createElement('canvas');
      canvas.width = dstW;
      canvas.height = dstH;
      canvas.getContext('2d').drawImage(img, 0, 0, dstW, dstH);
      canvas.toBlob(blob => {
        if (blob) {
          const newName = file.name.replace(/\.\w+$/, '.jpg');
          console.log('[Photo Normalize]', file.type || 'unknown',
            `${srcW}\u00d7${srcH} ${Math.round(file.size/1024)}KB`,
            '\u2192 JPEG',
            `${dstW}\u00d7${dstH} ${Math.round(blob.size/1024)}KB`);
          resolve(new File([blob], newName, { type: 'image/jpeg' }));
        } else reject(new Error('Canvas toBlob failed'));
      }, 'image/jpeg', NORMALIZE_QUALITY);
    } catch (err) { reject(err); }
    finally { URL.revokeObjectURL(url); }
  };
  img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Unsupported image format')); };
  img.src = url;
});

// Check photo print quality — returns { level: 'good'|'ok'|'low', message, dpi }
export const checkPhotoQuality = (file, layout) => new Promise((resolve) => {
  const url = URL.createObjectURL(file);
  const img = new window.Image();
  img.onload = () => {
    URL.revokeObjectURL(url);
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    const pixels = Math.max(w, h);
    let requiredHigh, requiredMed, layoutName, minRecommended;
    if (layout === 'photo-spread') {
      requiredHigh = 1500; requiredMed = 900;
      layoutName = 'a full page'; minRecommended = '1500\u00D72000px or larger';
    } else if (layout === 'story-page') {
      requiredHigh = 1200; requiredMed = 700;
      layoutName = 'a page header'; minRecommended = '1200\u00D7800px or larger';
    } else {
      requiredHigh = 900; requiredMed = 500;
      layoutName = 'print'; minRecommended = '900\u00D7700px or larger';
    }
    if (pixels >= requiredHigh) {
      resolve({ level: 'good', message: null, width: w, height: h });
    } else if (pixels >= requiredMed) {
      resolve({ level: 'ok', message: `This photo is ${w}\u00D7${h}px \u2014 it may appear slightly soft when printed as ${layoutName}. For the sharpest result, use a photo at least ${minRecommended}.`, width: w, height: h });
    } else {
      const isFullPage = layout === 'photo-spread';
      const isHeader = layout === 'story-page';
      let suggestion = '';
      if (isFullPage) {
        suggestion = ' Try switching to "Photo Header" or "Classic" layout \u2014 your photo will print smaller and look sharper.';
      } else if (isHeader) {
        suggestion = ' Try switching to "Classic" layout \u2014 your photo will print smaller and look sharper.';
      }
      resolve({
        level: 'low',
        message: `\u26A0\uFE0F This photo is ${w}\u00D7${h}px \u2014 it will likely look blurry when printed as ${layoutName}. For best results, use a photo at least ${minRecommended}. Photos saved from social media or screenshots are often too small.${suggestion}`,
        width: w, height: h
      });
    }
  };
  img.onerror = () => { URL.revokeObjectURL(url); resolve({ level: 'good', message: null }); };
  img.src = url;
});
